// Hardcoded fixtures for the sales banking demo (company.is_demo === true).
// The real e-banking components import these and use them — instead of
// dispatching to the backend — when the active company is a demo account
// (see the `user/isDemoAccount` getter). Pure data, no logic.

// Random-looking French IBAN (FR + 25 chars). Not a real account.
export const DEMO_IBAN = 'FR76 3000 4000 0500 0012 3456 789';
const DEMO_BIC = 'TRZOFR21XXX';

// Sales demo: fake destination for the treasury-investment funding transfer
// shown on the "ALIMENTER VOTRE COMPTE DE PLACEMENT" screen.
export const DEMO_TREASURY_IBAN = 'FR7617338000030693490317401';
export const DEMO_TREASURY_BIC = 'MEMOFRP2XXX';

// Bank account rows as returned by `bank/actionGetBanksAndAccountsList`
// (consumed by Accounts.vue `equalsBankAccounts`). CHF dropped per UI decision.
export const DEMO_BANK_ACCOUNTS = [
  {
    id: 'demo-acc-eur',
    accountId: 'demo-acc-eur',
    iban: DEMO_IBAN,
    bic: DEMO_BIC,
    balance: 35134.23,
    currency: 'EUR',
    name: 'Compte principal',
    custom_name: 'Compte principal',
  },
  {
    id: 'demo-acc-usd',
    accountId: 'demo-acc-usd',
    iban: DEMO_IBAN,
    bic: DEMO_BIC,
    balance: 38292.38,
    currency: 'USD',
    name: 'Compte USD',
  },
  {
    id: 'demo-acc-gbp',
    accountId: 'demo-acc-gbp',
    iban: DEMO_IBAN,
    bic: DEMO_BIC,
    balance: 29292.29,
    currency: 'GBP',
    name: 'Compte GBP',
  },
];

// Currency-account tiles for the demo "Compte principal" overview
// (DemoAccounts.vue). Shape matches Accounts.vue `currencyAccounts`:
// `{currency, label, amount, flag}`, derived from the same fake accounts.
const DEMO_CURRENCY_FLAGS: Record<string, string> = {
  EUR: 'eu',
  USD: 'us',
  GBP: 'gb',
};
export const DEMO_CURRENCY_ACCOUNTS = DEMO_BANK_ACCOUNTS.map((acc) => ({
  currency: acc.currency,
  label: 'Disponible',
  amount: acc.balance.toLocaleString('fr-FR', {
    style: 'currency',
    currency: acc.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }),
  flag:
    DEMO_CURRENCY_FLAGS[acc.currency] ?? acc.currency.slice(0, 2).toLowerCase(),
}));

// One bank with the `equals` aggregator (Accounts.vue looks it up by aggregator).
export const DEMO_BANK = {
  id: 'demo-equals-bank',
  name: 'Okimia',
  custom_name: 'Okimia',
  aggregator: 'equals',
  accounts: DEMO_BANK_ACCOUNTS,
};

// Second demo bank, connected through EBICS. `type: 'ebics'` is what
// `isEbics()` reads (accountsDDOptions copies it from the parent bank), and it
// is what unlocks the EBICS-only paths in demo — most notably the batch
// transfer, whose source-account selector accepts EBICS accounts only.
const DEMO_EBICS_IBAN = 'FR76 3000 1007 9412 3456 7890 185';
const DEMO_EBICS_BIC = 'BDFEFRPPXXX';
const DEMO_EBICS_BANK_NAME = 'Banque EBICS';

export const DEMO_EBICS_BANK_ACCOUNTS = [
  {
    id: 'demo-acc-ebics-eur',
    accountId: 'demo-acc-ebics-eur',
    iban: DEMO_EBICS_IBAN,
    bic: DEMO_EBICS_BIC,
    balance: 128450.77,
    currency: 'EUR',
    name: 'Compte courant EBICS',
    custom_name: 'Compte courant EBICS',
  },
];

// Company source accounts in the dropdown shape produced by
// `bank/accountsDDOptions` (consumed by MakeATransfer / SinglePayment /
// BatchPayment as the "compte source" selector). Built from the same fake
// accounts: the Equals bank first (default pick for a single transfer), then
// the EBICS one.
export const DEMO_COMPANY_ACCOUNTS_DD = [
  {
    name: 'Okimia',
    id: 'demo-equals-bank',
    showLogo: true,
    param: {selectAll: true},
    list: DEMO_BANK_ACCOUNTS.map((acc) => ({
      name: acc.custom_name || acc.name,
      id: acc.id,
      parentId: 'demo-equals-bank',
      aggregator: 'equals',
      type: '',
      parentName: 'Okimia',
      level: 1,
      deepest: true,
      iban: acc.iban,
      balance: acc.balance,
      currency: acc.currency,
      currency_iso: acc.currency,
      showLogo: false,
      param: {selectAll: false},
    })),
  },
  {
    name: DEMO_EBICS_BANK_NAME,
    id: 'demo-ebics-bank',
    showLogo: true,
    param: {selectAll: true},
    list: DEMO_EBICS_BANK_ACCOUNTS.map((acc) => ({
      name: acc.custom_name || acc.name,
      id: acc.id,
      parentId: 'demo-ebics-bank',
      aggregator: '',
      type: 'ebics',
      parentName: DEMO_EBICS_BANK_NAME,
      level: 1,
      deepest: true,
      iban: acc.iban,
      balance: acc.balance,
      currency: acc.currency,
      currency_iso: acc.currency,
      showLogo: false,
      param: {selectAll: false},
    })),
  },
];

// Raw card shape from `cards/actionGetCardsList` / `actionGetCardById`.
// `user_id` is filled in by the consumer with the active user's id so the
// card lands in the "Vos cartes" tab. Usage = total - available = 7 900 €.
export const DEMO_CARD_RAW = {
  id: 'demo-card-1',
  user_id: '',
  card_number_last4: '4242',
  name: 'Carte de test',
  status: 'ACTIVE',
  card_type: 'individual_card',
  expiry_month: 12,
  expiry_year: 2029,
  limits: [
    {
      type: 'spending_limit',
      metadata: {
        amount: 10000,
        available: {amount: 2100},
        currencyCode: 'EUR',
      },
    },
  ],
};

// Shared card example (TT-6732) — same raw shape as the API card, plus the
// shared-card fields the frontend expects (provisional contract, see
// `@/data/cardTypes`). `user_id` is filled by the consumer with the active user.
export const DEMO_SHARED_CARD_RAW = {
  id: 'demo-card-shared-1',
  user_id: '',
  card_number_last4: '7821',
  name: 'Marketing Ads',
  status: 'ACTIVE',
  card_type: 'budget_card',
  expiry_month: 9,
  expiry_year: 2028,
  notified_user_id: 'demo-notified-user-1',
  notified_user_email: 'camille.durand@exemple.fr',
  budget_id: 'demo-main-budget',
  limits: [
    {
      type: 'spending_limit',
      metadata: {
        amount: 5000,
        available: {amount: 3200},
        currencyCode: 'EUR',
      },
    },
  ],
};

// Owner id used for cards that belong to another employee, so they show under
// "Cartes de l'entreprise" but not "Vos cartes".
export const DEMO_OTHER_USER_ID = 'demo-other-user';

// Demo team members for the card-creation dropdowns (holder / validator).
// Tuple layout expected by the UI: [1]=nom, [2]=prénom, [3]=email, [7]=id.
export const DEMO_EMPLOYEES: (string | null)[][] = [
  [null, 'Durand', 'Camille', 'camille.durand@exemple.fr', null, null, null, 'demo-notified-user-1'],
  [null, 'Bernard', 'Lucas', 'lucas.bernard@exemple.fr', null, null, null, 'demo-notified-user-2'],
  [null, 'Martin', 'Julie', 'julie.martin@exemple.fr', null, null, null, DEMO_OTHER_USER_ID],
];

const demoLimit = (amount: number, available: number) => [
  {type: 'spending_limit', metadata: {amount, available: {amount: available}, currencyCode: 'EUR'}},
];

// Extra demo cards to exercise every tab & status:
// - user_id: '' → filled with the active user (→ Vos cartes + entreprise);
// - user_id: DEMO_OTHER_USER_ID → another employee (→ entreprise only);
// - REPLACED/TERMINATED land in "Archivées" (ACTIVE/SUSPENDED/UNACTIVATED stay live).
export const DEMO_EXTRA_CARDS_RAW = [
  // Second shared card the current user has access to — suspended, to show a
  // non-active status on a shared card.
  {
    id: 'demo-card-shared-2',
    user_id: '',
    card_number_last4: '3390',
    name: 'Abonnements SaaS',
    status: 'SUSPENDED',
    card_type: 'budget_card',
    expiry_month: 3,
    expiry_year: 2029,
    notified_user_id: 'demo-notified-user-1',
    notified_user_email: 'camille.durand@exemple.fr',
    budget_id: 'demo-main-budget',
    limits: demoLimit(2000, 1450),
  },
  // Individual card of another employee (entreprise only).
  {
    id: 'demo-card-other-1',
    user_id: DEMO_OTHER_USER_ID,
    card_number_last4: '1188',
    name: 'Julie Martin',
    status: 'ACTIVE',
    card_type: 'individual_card',
    expiry_month: 7,
    expiry_year: 2027,
    notified_user_id: DEMO_OTHER_USER_ID,
    notified_user_email: 'julie.martin@exemple.fr',
    limits: demoLimit(3000, 2600),
  },
  // Shared card the current user has no access to (entreprise only).
  {
    id: 'demo-card-other-2',
    user_id: DEMO_OTHER_USER_ID,
    card_number_last4: '5502',
    name: 'Frais de déplacement',
    status: 'ACTIVE',
    card_type: 'budget_card',
    expiry_month: 11,
    expiry_year: 2028,
    notified_user_id: 'demo-notified-user-2',
    notified_user_email: 'lucas.bernard@exemple.fr',
    budget_id: 'demo-main-budget',
    limits: demoLimit(4000, 900),
  },
  // Not activated yet.
  {
    id: 'demo-card-unactivated',
    user_id: '',
    card_number_last4: '4021',
    name: 'Nouvelle carte',
    status: 'UNACTIVATED',
    card_type: 'individual_card',
    expiry_month: 5,
    expiry_year: 2030,
    limits: demoLimit(500, 500),
  },
  // Archived — terminated (shared).
  {
    id: 'demo-card-terminated',
    user_id: DEMO_OTHER_USER_ID,
    card_number_last4: '6644',
    name: 'Campagne 2025',
    status: 'TERMINATED',
    card_type: 'budget_card',
    expiry_month: 12,
    expiry_year: 2026,
    notified_user_id: 'demo-notified-user-1',
    notified_user_email: 'camille.durand@exemple.fr',
    budget_id: 'demo-main-budget',
    limits: demoLimit(8000, 0),
  },
  // Archived — replaced.
  {
    id: 'demo-card-replaced',
    user_id: '',
    card_number_last4: '9310',
    name: 'Ancienne carte',
    status: 'REPLACED',
    card_type: 'individual_card',
    expiry_month: 4,
    expiry_year: 2026,
    limits: demoLimit(1500, 300),
  },
];

// Full set backing the demo cards list / detail pages.
export const DEMO_LIST_CARDS_RAW = [
  DEMO_CARD_RAW,
  DEMO_SHARED_CARD_RAW,
  ...DEMO_EXTRA_CARDS_RAW,
];

// Fake UUID used only when the demo card links to the transactions page.
// The transactions list is fetched from the real backend, which expects a
// UUID-shaped `cardId`; the human-friendly `demo-card-1` id would be rejected.
// It matches no real card, so the filtered list simply comes back empty.
export const DEMO_CARD_UUID = '00000000-0000-4000-8000-000000000001';

// Flattened single card for the demo card detail view (DemoCards.vue), in the
// shape its template/FygrCard expect. Limit usage = total - available = 7 900 €.
export const DEMO_CARD = {
  holderName: DEMO_CARD_RAW.name,
  last4: DEMO_CARD_RAW.card_number_last4,
  expDate: `${String(DEMO_CARD_RAW.expiry_month).padStart(2, '0')}/${String(
    DEMO_CARD_RAW.expiry_year,
  ).slice(-2)}`,
  cvv: '123', // matches DEMO_CARD_REVEAL.cvv (declared below)
  brand: 'visa' as const,
  typeLabel: 'Virtuelle',
  paymentLimitTotal: DEMO_CARD_RAW.limits[0].metadata.amount,
  paymentLimitUsed:
    DEMO_CARD_RAW.limits[0].metadata.amount -
    DEMO_CARD_RAW.limits[0].metadata.available.amount,
  currencyCode: DEMO_CARD_RAW.limits[0].metadata.currencyCode,
};

// Spending / transaction limits (SpendingLimits.vue, shape `{id, metadata}`).
export const DEMO_SPENDING_LIMITS = [
  {
    id: 'demo-spending-limit-1',
    metadata: {
      amount: 10000,
      limitWindow: 'MONTH',
      active: true,
      currencyCode: 'EUR',
    },
  },
];
export const DEMO_TRANSACTION_LIMITS = [
  {
    id: 'demo-transaction-limit-1',
    metadata: {amount: 2000, active: true, currencyCode: 'EUR'},
  },
];

// Transfer batch orders shown in "Opérations à valider" (Payments / Validate.vue).
// Parent-level rows: the "À valider" table shows label / total_amount / source.
export const DEMO_TRANSFER_ORDERS = [
  {
    id: 'demo-invoice-1',
    source_type: 'manual',
    source_label: 'Facture',
    label: 'Fournisseur Démo SAS',
    beneficiary: 'Fournisseur Démo SAS',
    effective_date: '2026-06-30',
    total_amount: 1250.0,
    total_transfer_numbers: 1,
    currency: 'EUR',
    iban: 'FR76 3000 4000 0500 0098 7654 321',
    transfer_orders: [
      {
        account_destination: 'FR76 3000 4000 0500 0098 7654 321',
        currency_iso: 'EUR',
      },
    ],
    cache: {
      transaction: {amount: 1250.0, currencyCode: 'EUR'},
      cardAcceptor: {name: 'Fournisseur Démo SAS'},
    },
  },
  {
    id: 'demo-invoice-2',
    source_type: 'ftp',
    source_label: 'Import XML',
    source_file_name: 'ordres_paiement_fournisseurs.xml',
    label: 'Lot fournisseurs - Juin 2026',
    effective_date: '2026-06-28',
    total_amount: 18420.5,
    total_transfer_numbers: 3,
    currency: 'EUR',
    iban: 'FR76 3000 4000 0500 0011 2233 445',
    transfer_orders: [
      {
        beneficiary: 'Bureau Vallée SARL',
        account_destination: 'FR76 3000 4000 0500 0011 2233 445',
        amount: 7200.0,
        currency_iso: 'EUR',
      },
      {
        beneficiary: 'Transports Rapides SAS',
        account_destination: 'FR76 1027 8060 4100 0203 0450 187',
        amount: 6120.5,
        currency_iso: 'EUR',
      },
      {
        beneficiary: 'Cabinet Juridique Meyer',
        account_destination: 'DE89 3704 0044 0532 0130 00',
        amount: 5100.0,
        currency_iso: 'EUR',
      },
    ],
  },
  {
    id: 'demo-invoice-3',
    source_type: 'manual',
    source_label: 'Facture',
    label: 'Agence Créative Nova',
    beneficiary: 'Agence Créative Nova',
    effective_date: '2026-07-02',
    total_amount: 3600.0,
    total_transfer_numbers: 1,
    currency: 'EUR',
    iban: 'FR76 1741 8000 0100 0012 3456 705',
    transfer_orders: [
      {
        beneficiary: 'Agence Créative Nova',
        account_destination: 'FR76 1741 8000 0100 0012 3456 705',
        amount: 3600.0,
        currency_iso: 'EUR',
      },
    ],
  },
  {
    id: 'demo-invoice-4',
    source_type: 'manual',
    source_label: 'Note de frais',
    label: 'Remboursement déplacements - Équipe commerciale',
    beneficiary: 'Julien Moreau',
    effective_date: '2026-07-01',
    total_amount: 842.75,
    total_transfer_numbers: 1,
    currency: 'EUR',
    iban: 'FR76 3000 3021 4000 0501 2345 674',
    transfer_orders: [
      {
        beneficiary: 'Julien Moreau',
        account_destination: 'FR76 3000 3021 4000 0501 2345 674',
        amount: 842.75,
        currency_iso: 'EUR',
      },
    ],
  },
];

// Transfer batch orders shown in "En cours" (Payments / Ongoing.vue).
// Ongoing.vue flattens `transfer_orders`: each needs beneficiary / effective_date
// / amount / currency / status (SCHEDULED → "Programmé", else "En cours"); the
// parent supplies validator_name / source_label / source_file_name / currency.
export const DEMO_TRANSFER_ORDERS_IN_PROGRESS = [
  {
    id: 'demo-ongoing-1',
    state: 'in_progress',
    source_label: 'Import XML',
    source_file_name: 'salaires_juin_2026.xml',
    validator_name: 'Camille Durand',
    currency: 'EUR',
    transfer_orders: [
      {
        id: 'to-1',
        status: 'SCHEDULED',
        beneficiary: 'Léa Bernard',
        effective_date: '2026-07-05',
        amount: 3200.0,
        currency: 'EUR',
      },
      {
        id: 'to-2',
        status: 'SCHEDULED',
        beneficiary: 'Thomas Petit',
        effective_date: '2026-07-05',
        amount: 2950.0,
        currency: 'EUR',
      },
      {
        id: 'to-3',
        status: 'SCHEDULED',
        beneficiary: 'Sarah Lefèvre',
        effective_date: '2026-07-05',
        amount: 3400.0,
        currency: 'EUR',
      },
    ],
  },
  {
    id: 'demo-ongoing-2',
    state: 'in_progress',
    source_label: 'Facture',
    validator_name: 'Camille Durand',
    currency: 'EUR',
    transfer_orders: [
      {
        id: 'to-4',
        status: 'PROCESSING',
        beneficiary: 'Fournitures Pro SAS',
        effective_date: '2026-07-03',
        amount: 4820.0,
        currency: 'EUR',
      },
      {
        id: 'to-5',
        status: 'PROCESSING',
        beneficiary: 'Hosting Cloud EU',
        effective_date: '2026-07-03',
        amount: 1290.0,
        currency: 'EUR',
      },
    ],
  },
];

// Transfer batch orders shown in "Passés" (Payments / Passed.vue).
// Same shape as ongoing; status FAILED → "Échoué" (danger), else "Exécuté".
export const DEMO_TRANSFER_ORDERS_PROCESSED = [
  {
    id: 'demo-passed-1',
    state: 'processed',
    source_label: 'Import XML',
    source_file_name: 'ordres_paiement_mai_2026.xml',
    validator_name: 'Camille Durand',
    currency: 'EUR',
    transfer_orders: [
      {
        id: 'tp-1',
        status: 'PROCESSED',
        beneficiary: 'Bureau Vallée SARL',
        effective_date: '2026-05-30',
        amount: 7200.0,
        currency: 'EUR',
      },
      {
        id: 'tp-2',
        status: 'PROCESSED',
        beneficiary: 'Transports Rapides SAS',
        effective_date: '2026-05-30',
        amount: 6120.5,
        currency: 'EUR',
      },
      {
        id: 'tp-3',
        status: 'PROCESSED',
        beneficiary: 'EDF Entreprises',
        effective_date: '2026-05-28',
        amount: 1875.4,
        currency: 'EUR',
      },
    ],
  },
  {
    id: 'demo-passed-2',
    state: 'processed',
    source_label: 'Facture',
    validator_name: 'Julien Moreau',
    currency: 'EUR',
    transfer_orders: [
      {
        id: 'tp-4',
        status: 'PROCESSED',
        beneficiary: 'Agence Créative Nova',
        effective_date: '2026-05-22',
        amount: 3600.0,
        currency: 'EUR',
      },
      {
        id: 'tp-5',
        status: 'FAILED',
        beneficiary: 'Cabinet Juridique Meyer',
        effective_date: '2026-05-22',
        amount: 5100.0,
        currency: 'EUR',
      },
    ],
  },
  {
    id: 'demo-passed-3',
    state: 'processed',
    source_label: 'Note de frais',
    validator_name: 'Camille Durand',
    currency: 'EUR',
    transfer_orders: [
      {
        id: 'tp-6',
        status: 'PROCESSED',
        beneficiary: 'Sarah Lefèvre',
        effective_date: '2026-05-15',
        amount: 640.2,
        currency: 'EUR',
      },
      {
        id: 'tp-7',
        status: 'PROCESSED',
        beneficiary: 'Thomas Petit',
        effective_date: '2026-05-15',
        amount: 512.8,
        currency: 'EUR',
      },
      {
        id: 'tp-8',
        status: 'PROCESSED',
        beneficiary: 'Léa Bernard',
        effective_date: '2026-05-15',
        amount: 733.9,
        currency: 'EUR',
      },
    ],
  },
];

// Maps the backend `state` value (see TAB_TO_STATE in Payments.vue) to the demo
// fixtures for that tab, so all three tabs are populated in demo mode.
export const DEMO_TRANSFER_ORDERS_BY_STATE: Record<string, unknown[]> = {
  to_be_validated: DEMO_TRANSFER_ORDERS,
  in_progress: DEMO_TRANSFER_ORDERS_IN_PROGRESS,
  processed: DEMO_TRANSFER_ORDERS_PROCESSED,
};

// Fake result of `eBanking/actionParseTransferBatch` for the EBICS/Okimia upload
// demo ("Effectuer un virement" → drag-drop a file). No backend: whatever file is
// dropped, this multi-transfer batch (3 suppliers, 14 750 €) is shown so the demo
// looks like a realistic pain.001 import rather than a single transfer.
const DEMO_DEBITOR_IBAN = 'FR7630066109600002041720197';
export const DEMO_PARSED_BATCH = {
  transfers: [
    {
      amount: 8500.0,
      currency_iso: 'EUR',
      debitor_iban: DEMO_DEBITOR_IBAN,
      debitor_name: 'Fygr',
      creditor_iban: 'FR7614508084405051682580167',
      creditor_name: 'ACCENTURE FRANCE SAS',
      reference: 'FACT-2026-0341 PRESTATIONS CONSEIL JUIN 2026',
      execution_date: '2026-06-30',
      recipient_id: null,
    },
    {
      amount: 3250.0,
      currency_iso: 'EUR',
      debitor_iban: DEMO_DEBITOR_IBAN,
      debitor_name: 'Fygr',
      creditor_iban: 'FR7630003021610003820930145',
      creditor_name: 'ORANGE BUSINESS SERVICES SA',
      reference: 'FACT-OBS-2026-1187 TELECOM ET INFRA JUIN 2026',
      execution_date: '2026-06-30',
      recipient_id: null,
    },
    {
      amount: 3000.0,
      currency_iso: 'EUR',
      debitor_iban: DEMO_DEBITOR_IBAN,
      debitor_name: 'Fygr',
      creditor_iban: 'FR7613106001701082476300194',
      creditor_name: 'LYRECO FRANCE SAS',
      reference: 'FACT-LYR-2026-5892 FOURNITURES BUREAU T2 2026',
      execution_date: '2026-06-30',
      recipient_id: null,
    },
  ],
  total_amount: 14750.0,
  total_transfers: 3,
  currency_iso: 'EUR',
  cache_key: 'demo-cache',
  transfers_hash: 'demo-hash',
};

// Sales demo: stand-in for `GET company/<id>/transferBatchOrder/<batch_id>`, which feeds the two
// review screens of the "À valider" tab. Built from the fixture row itself, since demo mode never
// reaches the backend.
export function demoBatchDetail(row: any) {
  const transferType = row?.transfer_type ?? 'credit_transfer';
  const directDebit = transferType === 'direct_debit';
  const orders: any[] = Array.isArray(row?.transfer_orders)
    ? row.transfer_orders
    : [];

  const transfers = orders.map((order) => {
    const own = {iban: DEMO_DEBITOR_IBAN, name: 'Fygr'};
    const counterparty = {
      iban: order?.account_destination ?? '',
      name: order?.beneficiary ?? row?.beneficiary ?? '',
    };
    const debitor = directDebit ? counterparty : own;
    const creditor = directDebit ? own : counterparty;

    return {
      amount: Math.abs(Number(order?.amount ?? row?.total_amount ?? 0)),
      currency_iso: order?.currency_iso ?? row?.currency ?? 'EUR',
      debitor_iban: debitor.iban,
      debitor_name: debitor.name,
      creditor_iban: creditor.iban,
      creditor_name: creditor.name,
      reference: order?.reference ?? row?.label ?? '',
      execution_date: order?.effective_date ?? row?.effective_date ?? '',
    };
  });

  return {
    transfer_type: transferType,
    total_transfers: transfers.length,
    total_amount: Math.abs(Number(row?.total_amount ?? 0)),
    currency_iso: row?.currency ?? 'EUR',
    transfers,
  };
}

// Fake full card secrets shown by the "Détails"/"PIN" reveal in demo (no Marqeta).
export const DEMO_CARD_REVEAL = {
  pan: '4242 4242 4242 4242',
  cvv: '123',
  pin: '1234',
};

// The only code the demo OTP step accepts. 123456 → OK, anything else → KO.
export const DEMO_OTP_CODE = '123456';

// Stand-ins for what `eBanking/actionBatchInit` / `actionSinglePrepare` would
// return for an EBICS transfer: the 3SKey popup needs a token and a hash to
// sign. The hash is arbitrary but well-formed (64 hex chars, like a SHA-256) so
// a real 3SKey plugged in during a demo can actually sign it; the resulting
// signature is then discarded instead of being submitted.
export const DEMO_SKEY_TRANSFER_TOKEN = 'demo-transfer-token';
export const DEMO_SKEY_CONNECTION_ID = 'demo-ebics-connection';
export const DEMO_SKEY_HASH_TO_SIGN =
  'a3f1c07b5d2e498a6b0c1d8e4f27a95b3c6d0e1f8a72b4c95d3e6f0a1b2c3d4e';

// Fake beneficiaries (contreparties) listed on the "Bénéficiaires" tab in demo
// mode. Shape matches the raw counterparty rows returned by
// `counterparties/actionGetCounterPartiesList` and consumed by
// CounterpartiesList.vue (kind / legal_name|surname+first_name / current_amount /
// average_delay / average_payment_delay / account_counterparties[]).
function demoAccountCounterparty(
  cpId: string,
  iban: string,
  currency = 'EUR',
) {
  return {
    id: `${cpId}-acc-1`,
    counterparty_id: cpId,
    iban,
    bic: 'TRZOFR21XXX',
    currency_iso: currency,
    label: 'Compte principal',
    is_default: true,
    status: 'validated',
  };
}

export const DEMO_COUNTERPARTIES = [
  // --- Corporate (Morale) ---
  {
    id: 'demo-cp-1',
    kind: 'corporate',
    legal_name: 'Bureau Vallée SARL',
    scheme_name: 'siren',
    identification: '812345678',
    current_amount: '-7200',
    average_delay: '4',
    average_payment_delay: '32',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-1', 'FR76 3000 4000 0500 0011 2233 445'),
    ],
  },
  {
    id: 'demo-cp-2',
    kind: 'corporate',
    legal_name: 'Transports Rapides SAS',
    scheme_name: 'siren',
    identification: '523456789',
    current_amount: '-6120.5',
    average_delay: '2',
    average_payment_delay: '28',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-2', 'FR76 1027 8060 4100 0203 0450 187'),
    ],
  },
  {
    id: 'demo-cp-3',
    kind: 'corporate',
    legal_name: 'Agence Créative Nova',
    scheme_name: 'siren',
    identification: '901234567',
    current_amount: '-3600',
    average_delay: '0',
    average_payment_delay: '15',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-3', 'FR76 1741 8000 0100 0012 3456 705'),
    ],
  },
  {
    id: 'demo-cp-4',
    kind: 'corporate',
    legal_name: 'Cabinet Juridique Meyer',
    scheme_name: 'other',
    identification: 'DE-HRB-90210',
    current_amount: '-5100',
    average_delay: '7',
    average_payment_delay: '45',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-4', 'DE89 3704 0044 0532 0130 00'),
    ],
  },
  {
    id: 'demo-cp-5',
    kind: 'corporate',
    legal_name: 'Fournitures Pro SAS',
    scheme_name: 'siren',
    identification: '432109876',
    current_amount: '-4820',
    average_delay: '3',
    average_payment_delay: '30',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-5', 'FR76 3000 3021 4000 0501 2345 674'),
    ],
  },
  {
    id: 'demo-cp-6',
    kind: 'corporate',
    legal_name: 'Hosting Cloud EU',
    scheme_name: 'other',
    identification: 'IE-6398745K',
    current_amount: '-1290',
    average_delay: '0',
    average_payment_delay: '10',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-6', 'IE29 AIBK 9311 5212 3456 78'),
    ],
  },
  {
    id: 'demo-cp-7',
    kind: 'corporate',
    legal_name: 'EDF Entreprises',
    scheme_name: 'siren',
    identification: '552081317',
    current_amount: '-1875.4',
    average_delay: '1',
    average_payment_delay: '20',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-7', 'FR76 3000 2005 5000 0015 7845 Z02'),
    ],
  },
  {
    id: 'demo-cp-8',
    kind: 'corporate',
    legal_name: 'Orange Business',
    scheme_name: 'siren',
    identification: '380129866',
    current_amount: '-980',
    average_delay: '0',
    average_payment_delay: '18',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-8', 'FR76 1820 6000 3165 0781 2345 690'),
    ],
  },
  {
    id: 'demo-cp-9',
    kind: 'corporate',
    legal_name: 'Assurances Générales du Nord',
    scheme_name: 'siren',
    identification: '318766155',
    current_amount: '-2450',
    average_delay: '5',
    average_payment_delay: '35',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-9', 'FR76 3007 6020 2100 0345 6789 012'),
    ],
  },
  {
    id: 'demo-cp-10',
    kind: 'corporate',
    legal_name: 'Imprimerie du Centre',
    scheme_name: 'siren',
    identification: '790456123',
    current_amount: '-720',
    average_delay: '2',
    average_payment_delay: '25',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-10', 'FR76 1600 6000 0112 3456 7890 159'),
    ],
  },
  // --- Individual (Physique) ---
  {
    id: 'demo-cp-11',
    kind: 'individual',
    surname: 'Moreau',
    first_name: 'Julien',
    current_amount: '-842.75',
    average_delay: '0',
    average_payment_delay: '5',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-11', 'FR76 3000 3021 4000 0501 2345 674'),
    ],
  },
  {
    id: 'demo-cp-12',
    kind: 'individual',
    surname: 'Bernard',
    first_name: 'Léa',
    current_amount: '-3200',
    average_delay: '0',
    average_payment_delay: '3',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-12', 'FR76 3000 4000 0500 0033 4455 662'),
    ],
  },
  {
    id: 'demo-cp-13',
    kind: 'individual',
    surname: 'Petit',
    first_name: 'Thomas',
    current_amount: '-2950',
    average_delay: '0',
    average_payment_delay: '3',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-13', 'FR76 1027 8060 4100 0203 0450 998'),
    ],
  },
  {
    id: 'demo-cp-14',
    kind: 'individual',
    surname: 'Lefèvre',
    first_name: 'Sarah',
    current_amount: '-3400',
    average_delay: '0',
    average_payment_delay: '3',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-14', 'FR76 1741 8000 0100 0088 7766 553'),
    ],
  },
  {
    id: 'demo-cp-15',
    kind: 'individual',
    surname: 'Garcia',
    first_name: 'Manon',
    current_amount: '-560',
    average_delay: '1',
    average_payment_delay: '7',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-15', 'ES91 2100 0418 4502 0005 1332'),
    ],
  },
  {
    id: 'demo-cp-16',
    kind: 'individual',
    surname: 'Rossi',
    first_name: 'Marco',
    current_amount: '-1120',
    average_delay: '0',
    average_payment_delay: '6',
    account_counterparties: [
      demoAccountCounterparty('demo-cp-16', 'IT60 X054 2811 1010 0000 0123 456'),
    ],
  },
];

type DemoCounterparty = (typeof DEMO_COUNTERPARTIES)[number];

// A demo company pays real backend invoices, whose counterparty ids never match
// the fixture ids above. Map any id onto a stable fixture (same id → same
// beneficiary across popups and reloads) so the payment flows always find a
// counterparty carrying a ready IBAN instead of asking the user to create one.
export function getDemoCounterparty(counterpartyId?: string): DemoCounterparty {
  const direct = DEMO_COUNTERPARTIES.find((cp) => cp.id === counterpartyId);
  if (direct) return direct;
  const key = String(counterpartyId || '');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash + key.charCodeAt(i)) % DEMO_COUNTERPARTIES.length;
  }
  return DEMO_COUNTERPARTIES[hash];
}

export function getDemoCounterpartyName(cp: DemoCounterparty): string {
  return cp.legal_name || `${cp.surname || ''} ${cp.first_name || ''}`.trim();
}

// Beneficiary accounts in the shape MakeATransfer / SinglePayment expect for
// their `accountsCounterparty` prop (seeding it skips the counterparty +
// account-counterparty creation steps entirely).
export function buildDemoAccountsCounterparty(counterpartyId?: string) {
  const cp = getDemoCounterparty(counterpartyId);
  const name = getDemoCounterpartyName(cp);
  return (cp.account_counterparties || []).map((acc) => ({
    name,
    Iban: acc.iban,
    currency_iso: acc.currency_iso || 'EUR',
    label: acc.label || '',
    bic: acc.bic || '',
    iban_status: acc.status || '',
    equals_actual_name: null,
    account_counterparty_id: acc.id || '',
    counterparty_id: cp.id,
    kind: cp.kind || 'corporate',
  }));
}

// Demo beneficiaries in the `{id, name}` shape of
// `counterparties/actionGetCounterPartiesDDList`, for the "Contrepartie"
// selector of the manual transfer form.
export function getDemoCounterpartiesDD(): Array<{ id: string; name: string }> {
  return DEMO_COUNTERPARTIES.map((cp) => ({
    id: cp.id,
    name: getDemoCounterpartyName(cp),
  }));
}

// A counterparty id that belongs to the fixtures above (as opposed to a real
// backend row a demo company may also have).
export function isDemoCounterpartyId(counterpartyId?: string): boolean {
  return DEMO_COUNTERPARTIES.some((cp) => cp.id === counterpartyId);
}

// Same fixtures in the raw shape returned by
// `counterparties/actionGetBulkCounterpartyIbans` (consumed by BatchPayment).
export function getDemoBulkCounterpartyIbans(
  counterpartyIds: string[],
): Record<string, unknown[]> {
  const res: Record<string, unknown[]> = {};
  for (const id of counterpartyIds) {
    if (!id) continue;
    res[id] = getDemoCounterparty(id).account_counterparties || [];
  }
  return res;
}

// Filters the demo beneficiaries for the requested tab (`corporate`/`individual`)
// and search string, mirroring what the backend would return for a demo company.
export function getDemoCounterparties(kind = 'corporate', search = ''): unknown[] {
  const q = (search || '').trim().toLowerCase();
  return DEMO_COUNTERPARTIES.filter((cp) => {
    if (cp.kind !== kind) return false;
    if (!q) return true;
    const name =
      cp.kind === 'corporate'
        ? cp.legal_name || ''
        : `${cp.surname || ''} ${cp.first_name || ''}`;
    const iban = cp.account_counterparties?.[0]?.iban || '';
    return (
      name.toLowerCase().includes(q) ||
      iban.toLowerCase().replace(/\s+/g, '').includes(q.replace(/\s+/g, ''))
    );
  });
}
